import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Edit } from "lucide-react";

export default function AdminLessons() {
  const { courseId } = useParams();
  const [course, setCourse] = useState<any>(null);
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { data: c } = await supabase.from("courses").select("*").eq("id", courseId).maybeSingle();
    setCourse(c);
    const { data } = await supabase.from("lessons").select("*").eq("course_id", courseId).order("sort_order");
    setList(data || []);
  }
  useEffect(() => { if (courseId) load(); }, [courseId]);

  function newItem() {
    setEditing({ course_id: courseId, slug: "", title: "", content: "", video_url: "", published: true, sort_order: list.length });
    setOpen(true);
  }

  async function save() {
    const { id, ...rest } = editing;
    const op = id ? supabase.from("lessons").update(rest).eq("id", id) : supabase.from("lessons").insert(rest);
    const { error } = await op;
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setOpen(false); load(); }
  }

  async function del(id: string) {
    if (!confirm("Delete lesson?")) return;
    await supabase.from("lessons").delete().eq("id", id);
    load();
  }

  return (
    <div className="space-y-4">
      <Link to="/admin/courses" className="text-sm hover:underline">← Courses</Link>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Lessons - {course?.title}</h2>
        <Button onClick={newItem}>Add lesson</Button>
      </div>
      <div className="grid gap-3">
        {list.map((l) => (
          <Card key={l.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{l.sort_order + 1}. {l.title}</div>
                <div className="text-sm text-muted-foreground">{l.slug}</div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="ghost" onClick={() => { setEditing(l); setOpen(true); }}><Edit className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(l.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} lesson</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
                <div><Label>Sort order</Label><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: +e.target.value })} /></div>
              </div>
              <div><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><Label>Video URL (optional)</Label><Input value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} /></div>
              <div><Label>Content (Markdown)</Label><Textarea rows={12} value={editing.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })} /></div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
              <Button onClick={save} className="w-full">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
